import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Box, Typography } from '@mui/material';
import loadTurnstile from './loadTurnstile';
import config from '../../../config';

/**
 * Cloudflare Turnstile (Managed) widget.
 *
 * Renders a Cloudflare challenge and reports the resulting token through
 * `onVerify`. Tokens are single-use and short-lived, so the parent should
 * treat every fresh `onVerify(token)` as the source of truth and call
 * `ref.reset()` after a failed submission to request a new token.
 *
 * If `REACT_APP_TURNSTILE_SITE_KEY` is not configured the component renders
 * nothing and stays out of the way (so environments without a key keep working).
 */
const Turnstile = forwardRef(function Turnstile(
  { action, theme = 'light', onVerify, onExpire, onError, className },
  ref
) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [loadError, setLoadError] = useState(false);

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.reset(widgetIdRef.current);
      }
    },
    getResponse: () => {
      if (window.turnstile && widgetIdRef.current !== null) {
        return window.turnstile.getResponse(widgetIdRef.current);
      }
      return null;
    },
  }));

  useEffect(() => {
    if (!config.TURNSTILE_SITE_KEY) return undefined;

    let cancelled = false;

    loadTurnstile()
      .then((turnstile) => {
        if (
          cancelled ||
          !containerRef.current ||
          widgetIdRef.current !== null
        ) {
          return;
        }
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: config.TURNSTILE_SITE_KEY,
          action,
          theme,
          callback: (token) => onVerify && onVerify(token),
          'expired-callback': () => onExpire && onExpire(),
          'error-callback': () => {
            setLoadError(true);
            if (onError) onError();
          },
        });
      })
      .catch(() => {
        if (cancelled) return;
        setLoadError(true);
        if (onError) onError();
      });

    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current !== null) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (e) {
          // widget already gone; nothing to clean up
        }
        widgetIdRef.current = null;
      }
    };
    // Site key/action are stable for the lifetime of the form; mount once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!config.TURNSTILE_SITE_KEY) return null;

  return (
    <Box
      className={className}
      display="flex"
      flexDirection="column"
      alignItems="center"
      width="100%"
    >
      <div ref={containerRef} />
      {loadError && (
        <Typography variant="caption" color="error" sx={{ mt: 1 }}>
          Couldn't load the verification challenge. Please refresh the page and
          try again.
        </Typography>
      )}
    </Box>
  );
});

export default Turnstile;
